import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useLRDirectGate } from "@/contexts/LRDirectGateContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Crown, Check, Zap } from "lucide-react";
import { startCheckoutOrDetectExisting } from "@/lib/billing/changePlan";
import { ChangePlanDialog } from "@/components/billing/ChangePlanDialog";
import { purchasesEnabled, NATIVE_BILLING_NOTICE } from "@/lib/billing/purchaseGate";


/**
 * LR Direct paywall — Full Access ($99.99/mo per user).
 *
 * Shown to brokers who try to bid without full access. The discontinued
 * $249.99 tier is no longer sold; Full Access covers private shipper
 * networks and the open marketplace.
 *
 * Stripe webhook (supabase/functions/stripe-webhook) provisions the
 * subscription row on checkout.session.completed. The success redirect
 * lands on /billing-return, which polls until billing_mode resolves
 * before routing.
 */

export function LRDirectPaywallModal() {
  const { user, profile } = useAuth();
  const { open, closeGate } = useLRDirectGate();
  const [loading, setLoading] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);

  const targetTier = profile?.user_type === "carrier" ? "carrier_full" : "broker";

  const handleCheckout = async () => {
    if (!user || !profile) {
      toast.error("Please sign in to subscribe.");
      return;
    }
    setLoading(true);
    try {
      const successUrl = `${window.location.origin}/billing-return?source=lr-direct&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = window.location.href;

      const outcome = await startCheckoutOrDetectExisting({
        email: profile.email,
        seats: 1,
        userId: user.id,
        userType: targetTier,
        fmcsaData: profile.fmcsa_legal_name ? {
          legalName: profile.fmcsa_legal_name,
          dotNumber: profile.fmcsa_dot_number,
          mcNumber: profile.fmcsa_mc_number,
        } : undefined,
        successUrl,
        cancelUrl,
      });

      if (outcome.kind === "redirect") {
        window.location.href = outcome.url;
        return;
      }
      if (outcome.kind === "existing_subscription") {
        // Existing subscriber → modify the current subscription (prorated),
        // never create a second one.
        setChangeOpen(true);
        setLoading(false);
        return;
      }
      toast.error(outcome.message);
      setLoading(false);
    } catch (err: any) {
      console.error("[LRDirectPaywallModal] checkout error", err);
      toast.error(err?.message || "Could not start checkout. Please try again.");
      setLoading(false);
    }
  };


  const bullets = [
    "Bid on every LR Direct load you can see",
    "Open-marketplace shipper freight",
    "Private shipper networks (when invited)",
    "Priority bidding & enhanced lane analytics",
    "Cancel anytime from billing settings",
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) closeGate(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-brand-teal" />
            <DialogTitle>Upgrade to LR Direct</DialogTitle>
          </div>
          <DialogDescription>
            LR Direct gives you full bidding access — private shipper networks you've been
            invited to and open-marketplace freight.
          </DialogDescription>
        </DialogHeader>

        <div className="pt-2">
          <div className="relative rounded-xl border border-brand-teal bg-brand-teal/5 p-5 flex flex-col gap-4 shadow-md">
            <div className="absolute -top-3 left-4 px-2 py-0.5 rounded-full bg-brand-teal text-white text-[10px] font-bold uppercase tracking-wider">
              Full Access
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-brand-teal text-white">
                <Crown className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-foreground leading-tight">LaneRunner — Full Access</h3>
                {purchasesEnabled() && (
                  <div className="mt-1 flex items-baseline gap-1">
                    <span className="text-3xl font-extrabold text-foreground">$99.99</span>
                    <span className="text-xs text-muted-foreground">/ month per user</span>
                  </div>
                )}
              </div>
            </div>

            <ul className="space-y-2">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm text-foreground/90">
                  <Check className="h-4 w-4 text-brand-teal mt-0.5 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            {purchasesEnabled() ? (
              <Button
                onClick={handleCheckout}
                disabled={loading}
                className="w-full h-11 font-bold uppercase tracking-wide bg-brand-teal text-white hover:bg-brand-teal/90"
              >
                {loading ? "Redirecting…" : "Upgrade for $99.99/month"}
              </Button>
            ) : (
              <p className="text-xs text-muted-foreground text-center">{NATIVE_BILLING_NOTICE}</p>
            )}
          </div>
        </div>

        {purchasesEnabled() && (
          <p className="text-[11px] text-muted-foreground text-center pt-2">
            Secure checkout powered by Stripe. Cancel anytime.
          </p>
        )}

        <ChangePlanDialog
          open={changeOpen}
          onOpenChange={setChangeOpen}
          tier={targetTier}
          tierLabel="Full Access — $99.99/month per user"
          onChanged={closeGate}
        />
      </DialogContent>
    </Dialog>

  );
}
